// AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from './assets/logo.png';

function AdminDashboard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('isLoggedIn');
    const storedUsername = localStorage.getItem('username');
    
    if (!token) {
      navigate('/login');
      return;
    }

    setUsername(storedUsername || 'Admin');

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const managementSections = [
    {
      title: 'Manage Cars in Showroom',
      description: 'Add, edit, or remove vehicles from your showroom collection',
      path: '/upload',
      color: '#C7B381'
    },
    {
      title: 'Manage images',
      description: 'Add, edit, or remove images from your showroom collection',
      path: '/admin-image-uploader',
      color: '#C7B381'
    },
    {
      title: 'Manage Hiring',
      description: 'Handle car rental requests and manage hiring documents',
      path: '/hirings',
      color: '#A89968'
    },
    {
      title: 'Manage Lease to Own',
      description: 'Oversee lease-to-own programs and agreements',
      path: '/lease-to-own',
      color: '#C7B381'
    },
       {
      title: 'Manage Airport Service',
      description: 'Add, edit, or remove images from your showroom collection',
      path: '/admin-airport-service',
      color: '#C7B381'
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)',
      color: '#E8E8E8',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(15, 20, 25, 0.98)',
        backdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(199, 179, 129, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: 'clamp(1rem, 2vw, 1.5rem) clamp(1rem, 3vw, 2rem)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img 
              src={logo} 
              alt="ST Motors Logo" 
              style={{
                height: 'clamp(50px, 8vw, 60px)',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <div>
              <div style={{ 
                fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', 
                fontWeight: '600', 
                color: '#FFFFFF', 
                letterSpacing: '0.05em'
              }}>
                ST MOTORS
              </div>
              <div style={{
                fontSize: 'clamp(0.7rem, 1.4vw, 0.8rem)',
                color: '#C7B381',
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>
                Admin Dashboard
              </div>
            </div>
          </Link>

          {/* User Info & Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
            <div style={{ 
              fontSize: 'clamp(0.85rem, 1.7vw, 0.95rem)', 
              color: '#B8B8B8',
              display: isMobile ? 'none' : 'block'
            }}>
              Welcome, <span style={{ color: '#C7B381', fontWeight: '500' }}>{username}</span>
            </div>
            <button
              onClick={handleLogout}
              style={{
                background: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                color: '#ff6b6b',
                padding: 'clamp(0.6rem, 1.5vw, 0.75rem) clamp(1rem, 2vw, 1.5rem)',
                borderRadius: '6px',
                fontSize: 'clamp(0.8rem, 1.6vw, 0.9rem)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(220, 53, 69, 0.2)';
                e.target.style.borderColor = 'rgba(220, 53, 69, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(220, 53, 69, 0.1)';
                e.target.style.borderColor = 'rgba(220, 53, 69, 0.3)';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: 'clamp(3rem, 6vw, 5rem) clamp(1rem, 3vw, 2rem)'
      }}>
        {/* Welcome Section */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'clamp(3rem, 6vw, 5rem)'
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '300',
            color: '#FFFFFF',
            marginBottom: '1rem',
            letterSpacing: '-0.01em'
          }}>
            Admin <span style={{ fontWeight: '600', color: '#C7B381' }}>Control Center</span>
          </h1>
          <p style={{
            fontSize: 'clamp(0.95rem, 1.9vw, 1.15rem)',
            color: '#B8B8B8',
            maxWidth: '700px',
            margin: '0 auto',
            fontWeight: '300',
            lineHeight: '1.7'
          }}>
            Manage your entire showroom inventory, rental services, and lease programs from one centralized dashboard
          </p>
        </div>

        {/* Management Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 'clamp(1.5rem, 3vw, 2rem)',
          marginBottom: 'clamp(3rem, 6vw, 4rem)'
        }}>
          {managementSections.map((section, index) => (
            <Link
              key={index}
              to={section.path}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div
                style={{
                  background: 'rgba(15, 20, 25, 0.8)',
                  border: '1px solid rgba(199, 179, 129, 0.2)',
                  borderRadius: '12px',
                  padding: 'clamp(2rem, 4vw, 2.5rem)',
                  transition: 'all 0.4s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = section.color;
                  e.currentTarget.style.boxShadow = `0 16px 40px rgba(0, 0, 0, 0.4)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Decorative Corner Gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `radial-gradient(circle at top right, ${section.color}15, transparent)`,
                  pointerEvents: 'none'
                }}></div>

                {/* Icon */}
                <div style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                  marginBottom: '1.5rem',
                  filter: 'grayscale(0.2)'
                }}>
                  {section.icon}
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: 'clamp(1.2rem, 2.4vw, 1.5rem)',
                  fontWeight: '500',
                  color: '#FFFFFF',
                  marginBottom: '0.75rem',
                  letterSpacing: '-0.01em'
                }}>
                  {section.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                  color: '#B8B8B8',
                  lineHeight: '1.6',
                  fontWeight: '300',
                  marginBottom: '1.5rem'
                }}>
                  {section.description}
                </p>

                {/* Action Link */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: section.color,
                  fontSize: 'clamp(0.85rem, 1.7vw, 0.95rem)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}>
                  Manage
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

      </main>
    </div>
  );
}

export default AdminDashboard;